import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Check,
  Loader2,
  Download,
  Crown,
  Headphones,
  Sparkles,
  ArrowLeft,
  Music2,
  Play,
  Pause,
} from "lucide-react";
import { toast } from "sonner";

/**
 * PacksPage - Unified "Choose Your Plan" page
 *
 * Three options side-by-side:
 *  1. Stream Unlimited (existing subscription, $19/mo) - links to /subscribe
 *  2. Course Pack ($30 for 6 songs from one course - download + own forever)
 *  3. Pick Any 4 ($25 for any 4 songs across the library - download + own forever)
 */
export default function PacksPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [coursePacks, setCoursePacks] = useState([]);
  const [librarySongs, setLibrarySongs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState("plans"); // plans | course | pick4
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [pickedSongs, setPickedSongs] = useState(() => {
    // Restore picks from a prior session (e.g. after login redirect)
    try {
      const saved = JSON.parse(sessionStorage.getItem("any4_picks") || "[]");
      return Array.isArray(saved) ? saved : [];
    } catch {
      return [];
    }
  });
  const [checkingOut, setCheckingOut] = useState(false);
  // Click-wrap: user must actively agree to Terms + Privacy before purchasing
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // Live subscription pricing from API (so this page stays in sync with /subscribe)
  const [subPricing, setSubPricing] = useState({ regular: 9.99, founding: 5.99, foundingAvailable: false });

  // Inline sample player state
  const previewAudioRef = useRef(null);
  const [previewingId, setPreviewingId] = useState(null);

  // Persist picks so the login redirect doesn't lose them
  useEffect(() => {
    sessionStorage.setItem("any4_picks", JSON.stringify(pickedSongs));
  }, [pickedSongs]);

  // If user lands here with ?pick4=1 after login, jump straight to the picker
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("pick4") === "1") {
      setView("pick4");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        // Both endpoints are public so non-logged-in visitors can browse + sample
        const [courses, lib, plans] = await Promise.all([
          axios.get(`${API}/packs/courses`),
          axios.get(`${API}/packs/library`),
          axios.get(`${API}/subscription/plans`),
        ]);
        if (!cancelled) {
          setCoursePacks(courses.data.packs || []);
          setLibrarySongs(lib.data.songs || []);
          const founding = plans.data.founding_member_status || {};
          const deal = plans.data.deal || {};
          const byId = plans.data.plans_by_id || {};
          const regular = byId.unlimited_monthly?.price ?? 9.99;
          const foundingPrice = deal.active ? deal.deal_price : (byId.founding_monthly?.price ?? 5.99);
          setSubPricing({
            regular,
            founding: foundingPrice,
            foundingAvailable: !!founding.available,
          });
        }
      } catch (e) {
        console.error("Failed to load packs", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const requireLogin = (returnPath) => {
    toast.info("Create an account or log in to complete checkout. Your picks are saved.");
    navigate(`/login?redirect=${encodeURIComponent(returnPath)}`);
  };

  const startCourseCheckout = async (courseName) => {
    if (!agreedToTerms) {
      return toast.error("Please agree to the Terms of Service and Privacy Policy to continue.");
    }
    if (!user) return requireLogin(`/packs?course=${encodeURIComponent(courseName)}`);
    setCheckingOut(true);
    try {
      const resp = await axios.post(`${API}/packs/checkout`, {
        pack_type: "course",
        course_name: courseName,
        origin_url: window.location.origin,
      });
      window.location.href = resp.data.url;
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setCheckingOut(false);
    }
  };

  const startAny4Checkout = async () => {
    if (!agreedToTerms) {
      return toast.error("Please agree to the Terms of Service and Privacy Policy to continue.");
    }
    if (pickedSongs.length !== 4) {
      return toast.error(`Please select exactly 4 songs (you've selected ${pickedSongs.length}).`);
    }
    if (!user) return requireLogin("/packs?pick4=1");
    setCheckingOut(true);
    try {
      const resp = await axios.post(`${API}/packs/checkout`, {
        pack_type: "any4",
        song_ids: pickedSongs,
        origin_url: window.location.origin,
      });
      // Clear saved picks - we're done with them
      sessionStorage.removeItem("any4_picks");
      window.location.href = resp.data.url;
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setCheckingOut(false);
    }
  };

  const togglePick = (id) => {
    setPickedSongs((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 4) {
        toast.info("You can only pick 4 songs. Deselect one first.");
        return prev;
      }
      return [...prev, id];
    });
  };

  const playPreview = (contentId) => {
    // Toggle off if currently previewing this song
    if (previewingId === contentId) {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
      }
      setPreviewingId(null);
      return;
    }
    // Stop any existing playback
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    const audio = new Audio(`${API}/packs/preview/${contentId}`);
    audio.onended = () => setPreviewingId(null);
    audio.onerror = () => {
      toast.error("Preview unavailable for this song");
      setPreviewingId(null);
    };
    previewAudioRef.current = audio;
    setPreviewingId(contentId);
    audio.play().catch((err) => {
      console.error("Audio play failed", err);
      setPreviewingId(null);
    });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // --- Course Pack picker view ---
  if (view === "course") {
    return (
      <div className="container mx-auto px-6 py-12 max-w-5xl" data-testid="course-pack-picker">
        <Button variant="ghost" onClick={() => setView("plans")} className="mb-4" data-testid="back-to-plans-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to all plans
        </Button>
        <h1 className="font-serif text-3xl md:text-4xl font-bold mb-2">
          Choose Your <span className="text-primary">Course Pack</span>
        </h1>
        <p className="text-muted-foreground mb-8">
          6 songs from one course · $30 · download now, listen forever
        </p>

        {/* Click-wrap: Terms acceptance required before purchase */}
        <label
          className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer select-none mb-6 p-3 rounded-lg bg-secondary/40 border border-border"
          data-testid="course-pack-terms-acceptance"
        >
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="mt-1 h-4 w-4 accent-primary cursor-pointer flex-shrink-0"
            data-testid="course-pack-terms-checkbox"
          />
          <span>
            I have read and agree to the{" "}
            <Link to="/terms" target="_blank" className="text-primary underline hover:opacity-80">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" target="_blank" className="text-primary underline hover:opacity-80">
              Privacy Policy
            </Link>
            . I understand that downloads are licensed for personal use only, do not transfer
            ownership of any composition or recording, and that Bar Songz does not guarantee any
            law-school or bar-exam outcome.
          </span>
        </label>
        {coursePacks.length === 0 && (
          <p className="text-muted-foreground">No course packs available yet — check back soon.</p>
        )}
        <div className="grid md:grid-cols-2 gap-5">
          {coursePacks.map((pack) => (
            <Card key={pack.course_name} className="border-border" data-testid={`course-pack-${pack.course_name}`}>
              <CardHeader>
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="font-serif">{pack.course_name}</CardTitle>
                  <Badge variant="outline" className="border-primary text-primary">
                    {pack.pack_song_count} songs
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-5">
                  {(pack.preview_songs || pack.preview_titles.map((t) => ({ title: t }))).map((s, i) => {
                    const isPlaying = previewingId === s.content_id;
                    const songName = s.song_title || s.title;
                    const subject = s.subject;
                    return (
                      <li key={s.content_id || i} className="flex items-start gap-2 text-sm">
                        {s.content_id ? (
                          <button
                            type="button"
                            onClick={() => playPreview(s.content_id)}
                            className="flex-shrink-0 h-6 w-6 rounded-full bg-primary/10 hover:bg-primary/20 text-primary flex items-center justify-center transition-colors mt-0.5"
                            data-testid={`course-preview-${s.content_id}`}
                            title="Preview (1 minute)"
                          >
                            {isPlaying ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3 ml-0.5" />}
                          </button>
                        ) : (
                          <Music2 className="h-3.5 w-3.5 text-primary flex-shrink-0 mt-1" />
                        )}
                        <div className="min-w-0">
                          <p className="text-foreground/90 truncate">{songName}</p>
                          {subject && subject !== songName && (
                            <p className="text-xs text-muted-foreground truncate">{subject}</p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-2xl font-bold text-primary">${pack.price_usd}</span>
                  <Button
                    onClick={() => startCourseCheckout(pack.course_name)}
                    disabled={checkingOut || !agreedToTerms}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                    data-testid={`buy-course-pack-${pack.course_name}-btn`}
                  >
                    {checkingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buy Pack"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // --- Pick Any 4 view ---
  if (view === "pick4") {
    const byCourse = librarySongs.reduce((acc, s) => {
      const k = s.course_name || "Other";
      (acc[k] = acc[k] || []).push(s);
      return acc;
    }, {});

    return (
      <div className="container mx-auto px-6 py-12 max-w-5xl" data-testid="pick4-picker">
        <Button variant="ghost" onClick={() => setView("plans")} className="mb-4" data-testid="back-to-plans-btn-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to all plans
        </Button>
        <h1 className="font-serif text-3xl md:text-4xl font-bold mb-2">
          Pick <span className="text-primary">Any 4 Songs</span>
        </h1>
        <p className="text-muted-foreground mb-6">
          $25 · download now, listen forever · mix and match across courses
        </p>

        {/* Click-wrap: Terms acceptance required before purchase */}
        <label
          className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer select-none mb-6 p-3 rounded-lg bg-secondary/40 border border-border"
          data-testid="pick4-terms-acceptance"
        >
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="mt-1 h-4 w-4 accent-primary cursor-pointer flex-shrink-0"
            data-testid="pick4-terms-checkbox"
          />
          <span>
            I have read and agree to the{" "}
            <Link to="/terms" target="_blank" className="text-primary underline hover:opacity-80">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" target="_blank" className="text-primary underline hover:opacity-80">
              Privacy Policy
            </Link>
            . I understand that downloads are licensed for personal use only, do not transfer
            ownership of any composition or recording, and that Bar Songz does not guarantee any
            law-school or bar-exam outcome.
          </span>
        </label>

        {/* Sticky selection bar */}
        <div className="sticky top-0 z-20 -mx-6 px-6 py-3 bg-background/95 backdrop-blur border-b border-border mb-4 flex items-center justify-between">
          <span className="text-sm font-medium">
            Selected: <span className="text-primary">{pickedSongs.length}</span> of 4
          </span>
          <Button
            onClick={startAny4Checkout}
            disabled={checkingOut || pickedSongs.length !== 4 || !agreedToTerms}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
            data-testid="checkout-pick4-btn"
          >
            {checkingOut ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {user ? "Checkout · $25" : "Continue · $25"}
          </Button>
        </div>

        <div className="mb-6 p-3 rounded-lg bg-secondary/40 border border-border text-sm text-muted-foreground flex items-start gap-2">
          <Play className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
          <span>
            Tap the play button next to any song to hear a <strong>1-minute preview</strong>.
            {!user && " You can build your pack now — we'll ask you to create an account at checkout."}
          </span>
        </div>

        {Object.keys(byCourse).sort().map((course) => (
          <div key={course} className="mb-6">
            <h3 className="font-serif text-lg font-semibold mb-2">{course}</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {byCourse[course].map((s) => {
                const picked = pickedSongs.includes(s.content_id);
                const isPlaying = previewingId === s.content_id;
                return (
                  <div
                    key={s.content_id}
                    data-testid={`pick-song-${s.content_id}`}
                    className={`rounded-lg border p-3 transition-all ${
                      picked
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/40"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          playPreview(s.content_id);
                        }}
                        className="flex-shrink-0 h-8 w-8 rounded-full bg-primary/10 hover:bg-primary/20 text-primary flex items-center justify-center transition-colors"
                        data-testid={`preview-song-${s.content_id}`}
                        title="Preview (10 seconds)"
                      >
                        {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ml-0.5" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => togglePick(s.content_id)}
                        data-testid={`toggle-pick-${s.content_id}`}
                        className="text-left flex items-start gap-2 flex-1 min-w-0"
                      >
                        <div
                          className={`h-5 w-5 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center text-xs ${
                            picked ? "bg-primary text-primary-foreground" : "border border-muted-foreground"
                          }`}
                        >
                          {picked ? <Check className="h-3 w-3" /> : null}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {s.song_title || s.title}
                          </p>
                          {s.song_title && s.title && s.song_title !== s.title && (
                            <p className="text-xs text-muted-foreground truncate">{s.title}</p>
                          )}
                        </div>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // --- Default: 3 plan cards ---
  return (
    <div className="container mx-auto px-6 py-12 max-w-6xl" data-testid="packs-plans">
      <div className="text-center mb-10">
        <h1 className="font-serif text-4xl md:text-5xl font-bold mb-3">
          Choose Your <span className="text-primary">Plan</span>
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Stream everything for a low monthly rate, or own your study tracks forever with a one-time download pack.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Stream Unlimited */}
        <Card className="border-border relative flex flex-col" data-testid="plan-stream-unlimited">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Headphones className="h-5 w-5 text-primary" />
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Subscription
              </span>
            </div>
            <CardTitle className="font-serif text-2xl">Stream Unlimited</CardTitle>
            <p className="text-muted-foreground text-sm mt-1">
              Unlimited streaming of the full library. Cancel anytime.
            </p>
          </CardHeader>
          <CardContent className="flex flex-col flex-1">
            <div className="mb-5">
              <span className="text-4xl font-bold">${subPricing.foundingAvailable ? subPricing.founding : subPricing.regular}</span>
              <span className="text-muted-foreground">/month</span>
              {subPricing.foundingAvailable && (
                <p className="text-xs text-amber-500 mt-1">Founding price · regularly ${subPricing.regular}/mo</p>
              )}
            </div>
            <ul className="space-y-2 text-sm mb-6 flex-1">
              <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" /> Unlimited streaming, every song</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" /> New tracks added regularly</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" /> Listen on any device</li>
              <li className="flex gap-2 text-muted-foreground"><span className="w-4 inline-block">·</span> No downloads</li>
            </ul>
            <Button
              onClick={() => navigate("/subscribe")}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full w-full"
              data-testid="choose-subscription-btn"
            >
              Subscribe
            </Button>
          </CardContent>
        </Card>

        {/* Course Pack */}
        <Card className="border-primary border-2 relative flex flex-col shadow-lg" data-testid="plan-course-pack">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <Badge className="bg-primary text-primary-foreground hover:bg-primary">
              <Sparkles className="h-3 w-3 mr-1" />
              Best Value
            </Badge>
          </div>
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Crown className="h-5 w-5 text-primary" />
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                One-time · Download
              </span>
            </div>
            <CardTitle className="font-serif text-2xl">Course Pack</CardTitle>
            <p className="text-muted-foreground text-sm mt-1">
              6 songs from one course · $5/song · yours forever.
            </p>
          </CardHeader>
          <CardContent className="flex flex-col flex-1">
            <div className="mb-5">
              <span className="text-4xl font-bold">$30</span>
              <span className="text-muted-foreground"> /pack</span>
            </div>
            <ul className="space-y-2 text-sm mb-6 flex-1">
              <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" /> 6 MP3 downloads from one course</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" /> Listen offline forever</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" /> Curated topic coverage</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" /> No subscription required</li>
            </ul>
            <Button
              onClick={() => setView("course")}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full w-full"
              data-testid="choose-course-pack-btn"
            >
              Browse Course Packs
            </Button>
          </CardContent>
        </Card>

        {/* Pick Any 4 */}
        <Card className="border-border relative flex flex-col" data-testid="plan-any4">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Download className="h-5 w-5 text-primary" />
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                One-time · Download
              </span>
            </div>
            <CardTitle className="font-serif text-2xl">Pick Any 4</CardTitle>
            <p className="text-muted-foreground text-sm mt-1">
              Mix and match any 4 songs across the entire library.
            </p>
          </CardHeader>
          <CardContent className="flex flex-col flex-1">
            <div className="mb-5">
              <span className="text-4xl font-bold">$25</span>
              <span className="text-muted-foreground"> /pack</span>
            </div>
            <ul className="space-y-2 text-sm mb-6 flex-1">
              <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" /> 4 MP3 downloads of your choice</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" /> Across any course</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" /> Listen offline forever</li>
              <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" /> No subscription required</li>
            </ul>
            <Button
              onClick={() => setView("pick4")}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full w-full"
              data-testid="choose-any4-btn"
            >
              Build Your Pack
            </Button>
          </CardContent>
        </Card>
      </div>

      <p className="text-center text-xs text-muted-foreground mt-10">
        All downloads include your name as the licensee in the file metadata. For personal study use only.
      </p>
    </div>
  );
}
